import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { ClientContextBanner, ClientContextIndicator } from '../../components/accountant/ClientContextBanner';
import ReportToolbar from '../../components/ReportToolbar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus, 
  CircleNotch,
  MagnifyingGlass,
  Wallet,
  Bank,
  TrendUp,
  TrendDown,
  Scales,
  ArrowsClockwise,
  Eye,
  Receipt,
  Money,
  Warning,
  CheckCircle,
  CaretRight,
  FileText,
  CurrencyDollar,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import { Link, useNavigate } from 'react-router-dom';
import { AccountsInsights } from '../../components/reports/AccountsInsights';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accountTypeInfo = {
  asset: { label: 'Asset', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', icon: Wallet },
  liability: { label: 'Liability', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: Bank },
  equity: { label: 'Equity', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: Scales },
  income: { label: 'Income', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: TrendUp },
  expense: { label: 'Expense', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: TrendDown },
};

export default function AccountsPage() {
  const { user, hasPermission } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateBankOpen, setIsCreateBankOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  
  // Drill-down modals
  const [showReceivables, setShowReceivables] = useState(false);
  const [showPayables, setShowPayables] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Drill-down data
  const [receivablesData, setReceivablesData] = useState(null);
  const [payablesData, setPayablesData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  
  const [newAccount, setNewAccount] = useState({
    code: '',
    name: '',
    account_type: 'asset',
    description: ''
  });
  
  const [newBank, setNewBank] = useState({
    name: '',
    bank_name: '',
    account_number: '',
    swift_code: '',
    iban: '',
    opening_balance: 0
  });

  // COA Audit / Auto-Fix state
  const [coaAudit, setCoaAudit] = useState(null);          // null=not loaded, [] = clean
  const [coaAuditLoading, setCoaAuditLoading] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [selectedFixIds, setSelectedFixIds] = useState({}); // {account_id: boolean}
  const [fixLoading, setFixLoading] = useState(false);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const fetchCoaAudit = useCallback(async () => {
    setCoaAuditLoading(true);
    try {
      const res = await axios.get(`${API_URL}/coa/audit`);
      setCoaAudit(res.data);
      // pre-select every HIGH-confidence suggestion
      const next = {};
      (res.data.suggestions || []).forEach(s => {
        if (s.confidence === 'HIGH') next[s.account_id] = true;
      });
      setSelectedFixIds(next);
    } catch (err) {
      // silent — banner just won't appear
    } finally {
      setCoaAuditLoading(false);
    }
  }, []);

  const handleOpenAudit = async () => {
    if (!coaAudit) await fetchCoaAudit();
    setIsAuditOpen(true);
  };

  const handleApplyAutoFix = async () => {
    const ids = Object.keys(selectedFixIds).filter(k => selectedFixIds[k]);
    if (ids.length === 0) {
      toast.error('Select at least one account to fix');
      return;
    }
    setFixLoading(true);
    try {
      const res = await axios.post(`${API_URL}/coa/auto-fix`, {
        account_ids: ids,
        confidence_floor: 'LOW', // user explicitly chose these — allow any confidence
      });
      const count = res.data.summary.fixed_count;
      toast.success(`Auto-fixed ${count} account(s). Open Financial Reports → P&L to see the updated figures.`, { duration: 6000 });
      setIsAuditOpen(false);
      // Auto-recompute step: refetch the COA + re-audit so banners disappear,
      // and emit a global event so any other open page (P&L, Trial Balance,
      // Dashboard) can refresh its cached data.
      await Promise.all([fetchData(), fetchCoaAudit()]);
      window.dispatchEvent(new CustomEvent('coa:updated', { detail: { fixed_count: count } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-fix failed');
    } finally {
      setFixLoading(false);
    }
  };

  /** Quick Re-Type — inline admin-only dropdown on each COA row. */
  const handleQuickRetype = async (account, newType) => {
    if (!isAdmin || newType === account.account_type) return;
    try {
      await axios.patch(`${API_URL}/accounts/${account.id}`, { account_type: newType });
      toast.success(`${account.code} ${account.name}: ${account.account_type} → ${newType}`);
      await Promise.all([fetchData(), fetchCoaAudit()]);
      window.dispatchEvent(new CustomEvent('coa:updated', { detail: { fixed_count: 1 } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Quick re-type failed');
    }
  };

  const canCreate = hasPermission('accounts', 'create');
  const canEdit = hasPermission('accounts', 'edit');
  const canDelete = hasPermission('accounts', 'delete');
  
  // Edit/Delete states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, statsRes, bankRes] = await Promise.all([
        axios.get(`${API_URL}/accounts`),
        axios.get(`${API_URL}/modules/accounts/stats`),
        axios.get(`${API_URL}/accounts/bank`)
      ]);
      setAccounts(accountsRes.data);
      setStats(statsRes.data);
      setBankAccounts(bankRes.data);
    } catch (error) {
      toast.error('Failed to load accounts data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCoaAudit();
  }, [fetchData, fetchCoaAudit]);

  useEffect(() => {
    axios.get(`${API_URL}/accounts/receivables/details`).then((r) => setReceivablesData(r.data)).catch(() => {});
    axios.get(`${API_URL}/accounts/payables/details`).then((r) => setPayablesData(r.data)).catch(() => {});
    axios.get(`${API_URL}/accounts/revenue/details`).then((r) => setRevenueData(r.data)).catch(() => {});
  }, []);

  const handleInitialize = async () => {
    setInitLoading(true);
    try {
      await axios.post(`${API_URL}/accounts/initialize`);
      toast.success('Chart of Accounts initialized successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize accounts');
    } finally {
      setInitLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/accounts`, newAccount);
      toast.success('Account created successfully');
      setIsCreateOpen(false);
      setNewAccount({ code: '', name: '', account_type: 'asset', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateBank = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/accounts/bank`, null, { params: newBank });
      toast.success('Bank account created');
      setIsCreateBankOpen(false);
      setNewBank({ name: '', bank_name: '', account_number: '', swift_code: '', iban: '', opening_balance: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bank account');
    } finally {
      setFormLoading(false);
    }
  };

  // Drill-down handlers
  const handleShowReceivables = async () => {
    try {
      const res = await axios.get(`${API_URL}/accounts/receivables/details`);
      setReceivablesData(res.data);
      setShowReceivables(true);
    } catch (error) {
      toast.error('Failed to load receivables data');
    }
  };

  const handleShowPayables = async () => {
    try {
      const res = await axios.get(`${API_URL}/accounts/payables/details`);
      setPayablesData(res.data);
      setShowPayables(true);
    } catch (error) {
      toast.error('Failed to load payables data');
    }
  };

  const handleShowRevenue = async () => {
    try {
      const res = await axios.get(`${API_URL}/accounts/revenue/details`);
      setRevenueData(res.data);
      setShowRevenue(true);
    } catch (error) {
      toast.error('Failed to load revenue data');
    }
  };

  const handleShowReconciliation = async (account) => {
    try {
      const res = await axios.get(`${API_URL}/accounts/reconciliation/${account.id}`);
      setReconciliationData(res.data);
      setSelectedAccount(account);
      setShowReconciliation(true);
    } catch (error) {
      toast.error('Failed to load reconciliation data');
    }
  };

  // Edit account handler
  const handleEditAccount = (account) => {
    setEditAccount({
      id: account.id,
      code: account.code,
      name: account.name,
      account_type: account.account_type,
      description: account.description || ''
    });
    setIsEditOpen(true);
  };

  const handleUpdateAccount = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.patch(`${API_URL}/accounts/${editAccount.id}`, {
        code: editAccount.code,
        name: editAccount.name,
        description: editAccount.description
      });
      toast.success('Account updated successfully');
      setIsEditOpen(false);
      setEditAccount(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setFormLoading(false);
    }
  };

  // Delete account handler
  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setShowDeleteConfirm(true);
  };

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    setFormLoading(true);
    try {
      await axios.delete(`${API_URL}/accounts/${accountToDelete.id}`);
      toast.success('Account deleted successfully');
      setShowDeleteConfirm(false);
      setAccountToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         acc.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || acc.account_type === activeTab;
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="accounts-page">
      {/* Client Context Banner for Accountant Mode */}
      <ClientContextBanner />
      
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Chart of Accounts
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage your accounting structure and track balances
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && coaAudit && coaAudit.totals?.suggestions > 0 && (
            <Button
              variant="outline"
              onClick={handleOpenAudit}
              className="border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
              data-testid="open-coa-audit-btn"
            >
              <Warning size={16} className="mr-2" weight="bold" />
              {coaAudit.totals.suggestions} mistyped account{coaAudit.totals.suggestions === 1 ? '' : 's'}
            </Button>
          )}
          <Link to="/dashboard/accounts/journal">
            <Button variant="outline" data-testid="journal-entries-btn">
              Journal Entries
            </Button>
          </Link>
          <Link to="/dashboard/accounts/trial-balance">
            <Button variant="outline" data-testid="trial-balance-btn">
              Trial Balance
            </Button>
          </Link>
          {canCreate && (
            <>
              <Button variant="outline" onClick={() => setIsCreateBankOpen(true)} data-testid="add-bank-btn">
                <Bank size={16} className="mr-2" />
                Add Bank
              </Button>
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-account-btn">
                    <Plus size={18} className="mr-2" />
                    Add Account
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Account</DialogTitle>
                    <DialogDescription>
                      Add a new account to your chart of accounts
                    </DialogDescription>
                  </DialogHeader>
                  {/* Show which company this account is for */}
                  <ClientContextIndicator label="Creating in" />
                  <form onSubmit={handleCreateAccount} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Account Code</Label>
                        <Input
                          value={newAccount.code}
                          onChange={(e) => setNewAccount(p => ({ ...p, code: e.target.value }))}
                          placeholder="1000"
                          required
                          data-testid="new-account-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Type</Label>
                        <Select 
                          value={newAccount.account_type} 
                          onValueChange={(v) => setNewAccount(p => ({ ...p, account_type: v }))}
                        >
                          <SelectTrigger data-testid="new-account-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(accountTypeInfo).map(([key, info]) => (
                              <SelectItem key={key} value={key}>{info.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Account Name</Label>
                      <Input
                        value={newAccount.name}
                        onChange={(e) => setNewAccount(p => ({ ...p, name: e.target.value }))}
                        placeholder="Cash"
                        required
                        data-testid="new-account-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Input
                        value={newAccount.description}
                        onChange={(e) => setNewAccount(p => ({ ...p, description: e.target.value }))}
                        placeholder="Main operating cash account"
                        data-testid="new-account-description"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={formLoading} data-testid="create-account-submit">
                        {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Create Account'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </motion.div>

      {/* COA Health Banner */}
      {isAdmin && coaAudit && coaAudit.totals?.suggestions > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 flex flex-wrap items-center gap-3"
          data-testid="coa-health-banner"
        >
          <Warning size={22} weight="duotone" className="text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              Chart of Accounts Health Check — {coaAudit.totals.suggestions} account{coaAudit.totals.suggestions === 1 ? '' : 's'} look{coaAudit.totals.suggestions === 1 ? 's' : ''} mistyped
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {coaAudit.totals.by_confidence?.HIGH || 0} HIGH · {coaAudit.totals.by_confidence?.MEDIUM || 0} MEDIUM · {coaAudit.totals.by_confidence?.LOW || 0} LOW confidence suggestions detected. Fixing them keeps your Trial Balance, P&L, and Balance Sheet accurate.
            </p>
          </div>
          <Button size="sm" onClick={handleOpenAudit} data-testid="review-coa-audit-btn">
            Review &amp; Fix
          </Button>
        </motion.div>
      )}

      {/* COA Audit Modal */}
      <Dialog open={isAuditOpen} onOpenChange={setIsAuditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="coa-audit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning size={20} className="text-amber-600" weight="duotone" />
              Chart of Accounts Audit
            </DialogTitle>
            <DialogDescription>
              Review accounts whose <span className="font-semibold">account_type</span> disagrees with the
              standard 5-digit code convention and account-name keywords. Pre-selected
              <Badge variant="outline" className="mx-1 bg-emerald-500/10 text-emerald-700 border-emerald-500/30">HIGH</Badge>
              suggestions are safe to apply.
            </DialogDescription>
          </DialogHeader>

          {coaAuditLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
              <CircleNotch className="animate-spin" /> Scanning your Chart of Accounts…
            </div>
          ) : (
            <>
              {(coaAudit?.suggestions || []).length === 0 ? (
                <div className="text-center py-10">
                  <CheckCircle size={40} weight="duotone" className="mx-auto text-emerald-600 mb-3" />
                  <p className="text-muted-foreground">Your Chart of Accounts looks healthy — no mistyped accounts detected.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Scanned <b>{coaAudit?.totals?.accounts_scanned}</b> accounts</span>
                    <span>·</span>
                    <span><b>{coaAudit?.totals?.suggestions}</b> suggestions</span>
                    <span>·</span>
                    <span>{coaAudit?.totals?.by_confidence?.HIGH || 0} HIGH / {coaAudit?.totals?.by_confidence?.MEDIUM || 0} MEDIUM / {coaAudit?.totals?.by_confidence?.LOW || 0} LOW</span>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-2 py-2 w-10">
                            <input
                              type="checkbox"
                              data-testid="select-all-fixes"
                              checked={(coaAudit?.suggestions || []).every(s => selectedFixIds[s.account_id])}
                              onChange={(e) => {
                                const next = {};
                                if (e.target.checked) {
                                  (coaAudit?.suggestions || []).forEach(s => { next[s.account_id] = true; });
                                }
                                setSelectedFixIds(next);
                              }}
                            />
                          </th>
                          <th className="text-left px-3 py-2 text-xs font-medium">Account</th>
                          <th className="text-center px-3 py-2 text-xs font-medium">Current</th>
                          <th className="text-center px-3 py-2 text-xs font-medium">Suggested</th>
                          <th className="text-center px-3 py-2 text-xs font-medium">Confidence</th>
                          <th className="text-left px-3 py-2 text-xs font-medium">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(coaAudit?.suggestions || []).map(s => {
                          const cur = accountTypeInfo[s.current_type] || { label: s.current_type, color: 'bg-zinc-500/10 text-zinc-600' };
                          const sug = accountTypeInfo[s.suggested_type] || { label: s.suggested_type, color: 'bg-zinc-500/10 text-zinc-600' };
                          const confColor = s.confidence === 'HIGH'
                            ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                            : s.confidence === 'MEDIUM'
                              ? 'bg-amber-500/10 text-amber-700 border-amber-500/30'
                              : 'bg-rose-500/10 text-rose-700 border-rose-500/30';
                          return (
                            <tr key={s.account_id} data-testid={`audit-row-${s.account_id}`}>
                              <td className="px-2 py-2 align-top">
                                <input
                                  type="checkbox"
                                  data-testid={`fix-toggle-${s.account_id}`}
                                  checked={!!selectedFixIds[s.account_id]}
                                  onChange={(e) => setSelectedFixIds(p => ({ ...p, [s.account_id]: e.target.checked }))}
                                />
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-xs">{s.code}</div>
                                <div className="font-medium">{s.name}</div>
                              </td>
                              <td className="px-3 py-2 align-top text-center">
                                <Badge variant="outline" className={cur.color}>{cur.label || s.current_type}</Badge>
                              </td>
                              <td className="px-3 py-2 align-top text-center">
                                <Badge variant="outline" className={sug.color}>{sug.label || s.suggested_type}</Badge>
                              </td>
                              <td className="px-3 py-2 align-top text-center">
                                <Badge variant="outline" className={confColor}>{s.confidence}</Badge>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
                                  {s.reasons.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAuditOpen(false)} data-testid="close-audit-btn">Cancel</Button>
            {(coaAudit?.suggestions || []).length > 0 && (
              <Button
                onClick={handleApplyAutoFix}
                disabled={fixLoading || Object.values(selectedFixIds).filter(Boolean).length === 0}
                data-testid="apply-coa-fix-btn"
              >
                {fixLoading ? <CircleNotch className="animate-spin mr-2" /> : null}
                Apply Fix to {Object.values(selectedFixIds).filter(Boolean).length} account(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setOverviewOpen(true)}
          className="card-tactile rounded-lg p-5 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
          data-testid="total-accounts-card"
        >
          <div className="flex justify-between items-start">
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Total Accounts</p>
            <CaretRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-2xl font-heading font-semibold mt-1">{stats?.total_accounts || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">Click to view by type</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          onClick={handleShowReceivables}
          className="card-tactile rounded-lg p-5 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
          data-testid="receivables-card"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Receivables</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-emerald-600">
                {formatCurrency(receivablesData?.summary?.total_receivable || 0)}
              </p>
            </div>
            <CaretRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Click to view invoices</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={handleShowPayables}
          className="card-tactile rounded-lg p-5 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
          data-testid="payables-card"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Payables</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-red-600">
                {formatCurrency(payablesData?.summary?.total_payable || 0)}
              </p>
            </div>
            <CaretRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Click to view bills</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          onClick={handleShowRevenue}
          className="card-tactile rounded-lg p-5 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
          data-testid="revenue-card"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Revenue</p>
              <p className="text-2xl font-heading font-semibold mt-1 text-blue-600">
                {formatCurrency(revenueData?.summary?.total_revenue || 0)}
              </p>
            </div>
            <CaretRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Click to view income</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate('/dashboard/accounts/journal')}
          className="card-tactile rounded-lg p-5 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all group"
          data-testid="journal-entries-card"
        >
          <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Journal Entries</p>
          <p className="text-2xl font-heading font-semibold mt-1">{stats?.total_entries || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="text-emerald-600">{stats?.posted_entries || 0}</span> posted, <span className="text-amber-600">{stats?.draft_entries || 0}</span> draft
          </p>
        </motion.div>
      </div>

      {accounts.length > 0 && (
        <AccountsInsights accounts={accounts} overviewOpen={overviewOpen} onOverviewClose={() => setOverviewOpen(false)} />
      )}

      {/* Bank Accounts Section */}
      {bankAccounts.accounts?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg overflow-hidden"
        >
          <div className="p-4 border-b border-border bg-muted/30 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Bank size={20} className="text-primary" />
              <h3 className="font-semibold">Bank & Cash Accounts</h3>
            </div>
            <p className="text-sm">
              Total: <span className="font-semibold">{formatCurrency(bankAccounts.total_balance || 0)}</span>
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bankAccounts.accounts?.map(acc => (
                <div 
                  key={acc.id} 
                  className="p-4 border border-border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleShowReconciliation(acc)}
                  data-testid={`bank-acc-${acc.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {acc.is_cash ? <CurrencyDollar size={20} className="text-emerald-600" /> : <Bank size={20} className="text-blue-600" />}
                      <span className="font-medium">{acc.name}</span>
                    </div>
                    <Badge variant="outline" className="font-mono text-xs">{acc.code}</Badge>
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(acc.balance || 0)}</p>
                  {acc.bank_name && <p className="text-xs text-muted-foreground mt-1">{acc.bank_name}</p>}
                  {acc.account_number && <p className="text-xs text-muted-foreground font-mono">****{acc.account_number.slice(-4)}</p>}
                  <p className="text-xs text-primary mt-2">Click to reconcile</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Initialize Button (if no accounts) */}
      {accounts.length === 0 && canCreate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-8 text-center"
        >
          <ArrowsClockwise size={48} weight="duotone" className="mx-auto text-primary mb-4" />
          <h3 className="font-heading text-xl font-medium mb-2">Initialize Chart of Accounts</h3>
          <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
            Start with a standard chart of accounts including common asset, liability, equity, income, and expense accounts.
          </p>
          <Button onClick={handleInitialize} disabled={initLoading} data-testid="initialize-accounts-btn">
            {initLoading ? <CircleNotch className="animate-spin mr-2" size={18} /> : <Plus className="mr-2" size={18} />}
            Initialize Default Accounts
          </Button>
        </motion.div>
      )}

      {/* Search and Tabs */}
      {accounts.length > 0 && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="account-search"
              />
            </div>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="asset" data-testid="tab-asset">Assets</TabsTrigger>
              <TabsTrigger value="liability" data-testid="tab-liability">Liabilities</TabsTrigger>
              <TabsTrigger value="equity" data-testid="tab-equity">Equity</TabsTrigger>
              <TabsTrigger value="income" data-testid="tab-income">Income</TabsTrigger>
              <TabsTrigger value="expense" data-testid="tab-expense">Expense</TabsTrigger>
            </TabsList>

            <ReportToolbar
              testidPrefix="coa-toolbar"
              downloadBaseName="chart-of-accounts"
              exportUrl={(fmt) => {
                const params = new URLSearchParams();
                if (activeTab && activeTab !== 'all') params.set('account_type', activeTab);
                if (searchTerm) params.set('q', searchTerm);
                return `${process.env.REACT_APP_BACKEND_URL}/api/reports/chart-of-accounts.${fmt}${params.toString() ? '?' + params.toString() : ''}`;
              }}
            />

            <TabsContent value={activeTab} className="mt-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-tactile rounded-lg overflow-hidden"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Account Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAccounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No accounts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAccounts.map((account) => {
                        const typeInfo = accountTypeInfo[account.account_type];
                        const Icon = typeInfo?.icon || Wallet;
                        return (
                          <TableRow key={account.id} data-testid={`account-row-${account.id}`}>
                            <TableCell className="font-mono text-sm">{account.code}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${typeInfo?.color}`}>
                                  <Icon size={16} weight="duotone" />
                                </div>
                                <div>
                                  <p className="font-medium">{account.name}</p>
                                  {account.description && (
                                    <p className="text-xs text-muted-foreground">{account.description}</p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {isAdmin ? (
                                <Select
                                  value={account.account_type}
                                  onValueChange={(v) => handleQuickRetype(account, v)}
                                >
                                  <SelectTrigger
                                    className={`h-8 w-[120px] border ${typeInfo?.color || ''}`}
                                    data-testid={`account-type-select-${account.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <SelectValue>{typeInfo?.label || account.account_type}</SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(accountTypeInfo).map(([key, info]) => (
                                      <SelectItem key={key} value={key} data-testid={`type-option-${key}`}>{info.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className={typeInfo?.color}>
                                  {typeInfo?.label}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              <span className={account.balance < 0 ? 'text-red-600' : ''}>
                                {formatCurrency(account.balance)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end">
                                <RowActionsMenu
                                  testid={`account-${account.id}`}
                                  actions={[
                                    { id: 'view', label: 'View Ledger / Reconcile', icon: Eye, onClick: () => handleShowReconciliation(account) },
                                    canEdit && { id: 'edit', label: 'Edit Account', icon: PencilSimple, onClick: () => handleEditAccount(account) },
                                    canDelete && 'separator',
                                    canDelete && { id: 'delete', label: 'Delete Account', icon: Trash, onClick: () => handleDeleteClick(account), destructive: true },
                                  ].filter(Boolean)}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </motion.div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Receivables Drill-down Dialog */}
      <Dialog open={showReceivables} onOpenChange={setShowReceivables}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={24} className="text-emerald-600" />
              Accounts Receivable Details
            </DialogTitle>
            <DialogDescription>
              {receivablesData?.summary?.invoice_count || 0} unpaid invoices totaling {formatCurrency(receivablesData?.summary?.total_receivable || 0)}
            </DialogDescription>
          </DialogHeader>
          
          {receivablesData?.summary?.overdue_count > 0 && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg text-red-600">
              <Warning size={18} />
              <span className="font-medium">{receivablesData.summary.overdue_count} overdue ({formatCurrency(receivablesData.summary.overdue_amount)})</span>
            </div>
          )}
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receivablesData?.invoices?.map(inv => (
                <TableRow key={inv.id} className={inv.is_overdue ? 'bg-red-500/5' : ''}>
                  <TableCell className="font-mono">{inv.document_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell>{inv.date}</TableCell>
                  <TableCell>
                    {inv.due_date}
                    {inv.is_overdue && <Warning size={14} className="inline ml-1 text-red-600" />}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(inv.total)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(inv.amount_due)}</TableCell>
                  <TableCell>
                    <Badge className={inv.is_overdue ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}>
                      {inv.is_overdue ? 'Overdue' : inv.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceivables(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payables Drill-down Dialog */}
      <Dialog open={showPayables} onOpenChange={setShowPayables}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Money size={24} className="text-red-600" />
              Accounts Payable Details
            </DialogTitle>
            <DialogDescription>
              Total payable: {formatCurrency(payablesData?.summary?.total_payable || 0)}
            </DialogDescription>
          </DialogHeader>
          
          {payablesData?.grns?.length > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg mb-4">
              <p className="font-medium">GRN (Goods Received - Not Invoiced)</p>
              <p className="text-2xl font-bold">{formatCurrency(payablesData?.summary?.grn_total || 0)}</p>
              <p className="text-sm text-muted-foreground">{payablesData?.grns?.length} GRN records</p>
            </div>
          )}
          
          {payablesData?.purchase_invoices?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Due</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payablesData.purchase_invoices.map(inv => (
                  <TableRow key={inv.id} className={inv.is_overdue ? 'bg-red-500/5' : ''}>
                    <TableCell className="font-mono">{inv.document_number}</TableCell>
                    <TableCell>{inv.supplier_name}</TableCell>
                    <TableCell>{inv.date}</TableCell>
                    <TableCell>
                      {inv.due_date}
                      {inv.is_overdue && <Warning size={14} className="inline ml-1 text-red-600" />}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(inv.total)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(inv.amount_due)}</TableCell>
                    <TableCell>
                      <Badge className={inv.is_overdue ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}>
                        {inv.is_overdue ? 'Overdue' : inv.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No purchase invoices pending</p>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayables(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revenue Drill-down Dialog */}
      <Dialog open={showRevenue} onOpenChange={setShowRevenue}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendUp size={24} className="text-blue-600" />
              Revenue Details
            </DialogTitle>
            <DialogDescription>
              {revenueData?.summary?.invoice_count || 0} paid/partial invoices totaling {formatCurrency(revenueData?.summary?.total_revenue || 0)}
            </DialogDescription>
          </DialogHeader>
          
          {revenueData?.summary?.by_customer && Object.keys(revenueData.summary.by_customer).length > 0 && (
            <div className="mb-4">
              <p className="font-medium mb-2">By Customer</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(revenueData.summary.by_customer).map(([cust, data]) => (
                  <div key={cust} className="px-3 py-2 bg-muted/50 rounded-lg">
                    <p className="font-medium text-sm">{cust}</p>
                    <p className="text-lg font-bold">{formatCurrency(data.amount)}</p>
                    <p className="text-xs text-muted-foreground">{data.count} invoice(s)</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {revenueData?.invoices?.map(inv => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono">{inv.document_number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell>{inv.date}</TableCell>
                  <TableCell className="text-right">{formatCurrency(inv.total)}</TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">{formatCurrency(inv.amount_paid)}</TableCell>
                  <TableCell>
                    <Badge className={inv.status === 'paid' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-blue-500/10 text-blue-600'}>
                      {inv.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevenue(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={showReconciliation} onOpenChange={setShowReconciliation}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={24} className="text-primary" />
              Account Ledger & Reconciliation
            </DialogTitle>
            <DialogDescription>
              {selectedAccount?.name} ({selectedAccount?.code})
            </DialogDescription>
          </DialogHeader>
          
          {reconciliationData && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="p-3 bg-muted/50 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Opening</p>
                  <p className="text-lg font-bold">{formatCurrency(reconciliationData.summary.opening_balance)}</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Total Debits</p>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(reconciliationData.summary.total_debits)}</p>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Total Credits</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(reconciliationData.summary.total_credits)}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Closing Balance</p>
                  <p className="text-lg font-bold">{formatCurrency(reconciliationData.summary.closing_balance)}</p>
                </div>
              </div>
              
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-muted-foreground">
                  {reconciliationData.summary.total_transactions} transactions ({reconciliationData.summary.unreconciled_count} unreconciled)
                </p>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconciliationData.transactions?.map((txn, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{txn.date}</TableCell>
                        <TableCell className="font-mono text-sm">{txn.reference}</TableCell>
                        <TableCell className="text-sm">{txn.description}</TableCell>
                        <TableCell className="text-right font-mono">
                          {txn.debit > 0 ? formatCurrency(txn.debit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {txn.credit > 0 ? formatCurrency(txn.credit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {formatCurrency(txn.balance)}
                        </TableCell>
                        <TableCell>
                          {txn.reconciled ? (
                            <CheckCircle size={16} className="text-emerald-600" />
                          ) : (
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReconciliation(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Bank Account Dialog */}
      <Dialog open={isCreateBankOpen} onOpenChange={setIsCreateBankOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Bank Account</DialogTitle>
            <DialogDescription>Create a new bank account in your chart of accounts</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateBank} className="space-y-4">
            <div>
              <Label>Account Name *</Label>
              <Input
                value={newBank.name}
                onChange={(e) => setNewBank(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Business Checking"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={newBank.bank_name}
                  onChange={(e) => setNewBank(p => ({ ...p, bank_name: e.target.value }))}
                  placeholder="e.g., Chase Bank"
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input
                  value={newBank.account_number}
                  onChange={(e) => setNewBank(p => ({ ...p, account_number: e.target.value }))}
                  placeholder="****1234"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SWIFT Code</Label>
                <Input
                  value={newBank.swift_code}
                  onChange={(e) => setNewBank(p => ({ ...p, swift_code: e.target.value }))}
                  placeholder="CHASUS33"
                />
              </div>
              <div>
                <Label>IBAN</Label>
                <Input
                  value={newBank.iban}
                  onChange={(e) => setNewBank(p => ({ ...p, iban: e.target.value }))}
                  placeholder="AE12345..."
                />
              </div>
            </div>
            <div>
              <Label>Opening Balance</Label>
              <Input
                type="number"
                value={newBank.opening_balance}
                onChange={(e) => setNewBank(p => ({ ...p, opening_balance: parseFloat(e.target.value) || 0 }))}
                step="0.01"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setIsCreateBankOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading}>
                {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Create Bank Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>
              Update account details
            </DialogDescription>
          </DialogHeader>
          {editAccount && (
            <form onSubmit={handleUpdateAccount} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Code</Label>
                  <Input
                    value={editAccount.code}
                    onChange={(e) => setEditAccount(p => ({ ...p, code: e.target.value }))}
                    placeholder="1000"
                    required
                    data-testid="edit-account-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <Input
                    value={accountTypeInfo[editAccount.account_type]?.label || editAccount.account_type}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">Account type cannot be changed</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input
                  value={editAccount.name}
                  onChange={(e) => setEditAccount(p => ({ ...p, name: e.target.value }))}
                  placeholder="Cash"
                  required
                  data-testid="edit-account-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  value={editAccount.description}
                  onChange={(e) => setEditAccount(p => ({ ...p, description: e.target.value }))}
                  placeholder="Main operating cash account"
                  data-testid="edit-account-description"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="update-account-submit">
                  {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Update Account'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{accountToDelete?.name}</strong> ({accountToDelete?.code})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 bg-amber-500/10 rounded-lg text-amber-700 text-sm">
            <Warning size={16} className="inline mr-2" />
            Accounts with existing journal entries cannot be deleted. Consider deactivating instead.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={formLoading}
              data-testid="confirm-delete-account"
            >
              {formLoading ? <CircleNotch className="animate-spin" size={18} /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
